import { Skeleton, Typography } from "@packages/ds";
import styled, { css } from "styled-components";

import { Link as LinkDefault } from "ds/Button";
import { semiBoldAlias } from "Libs/theme";
import Metric from "src/organization/pages/project/environment/metrics/containers/Service/Metric";
import * as S from "src/organization/pages/project/environment/metrics/containers/Service/Metric/Metric.styles";
import ServiceSkeleton from "src/organization/pages/project/environment/metrics/containers/Service/Service.skeleton";
import * as ServiceStyles from "src/organization/pages/project/environment/metrics/containers/Service/Service.styles";
import * as V from "src/organization/pages/project/environment/metrics/resources/views.styles";

export const Container = styled.div`
  margin-top: 24px;
  overflow: hidden;
`;

export const Header = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 8px;
`;

export const Title = styled(Typography.Title)`
  margin-bottom: 11px;
  text-transform: capitalize;
`;

export const HeaderRight = styled.div`
  margin-left: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

export const Timeframe = styled.div`
  background-color: var(--section-snow-background-color, var(--snow));
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding-top: 4px;
  padding-right: 8px;
  padding-bottom: 4px;
  padding-left: 8px;
  font-family: Open Sans;
  font-style: normal;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  color: var(--section-slate-color, var(--slate));
  border-radius: 2px;
`;

export const ServiceMetric = styled(Metric)`
  ${S.Metadata} {
    flex-direction: column;
    gap: 0;
    align-items: flex-start;
  }
`;

export const ServiceMetricsSkeleton = styled(ServiceSkeleton)`
  ${ServiceStyles.Header} {
    display: none;
  }

  ${S.Row}:first-child {
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;

    ${Skeleton.Square} {
      height: 22px;
    }
  }
`;

export const EmptyMetrics = styled.p`
  ${semiBoldAlias()};
  font-size: 13px;
  line-height: 18px;
  text-align: center;
  color: var(--header-ebony-color, var(--ebony));
`;

export const Link = styled(LinkDefault)`
  font-size: 13px;
`;

const verticalSeparator = (separator = 16) => css`
  position: relative;
  &:before {
    content: "";
    position: absolute;
    left: -${separator}px;
    height: 100%;
    width: 1px;
    background-color: var(
      --section-periwinkle-grey-background-color,
      var(--periwinkle-grey)
    );
  }
`;

const horizontalSeparator = (
  leftShift = 0,
  addedWidth = 32,
  bottom = 13
) => css`
  position: relative;
  &::after {
    content: "";
    position: absolute;
    bottom: -${bottom}px;
    left: -${leftShift}px;
    height: 1px;
    width: calc(100% + ${addedWidth}px);
    background-color: var(
      --section-periwinkle-grey-background-color,
      var(--periwinkle-grey)
    );
  }
`;

export const Grid = styled.div`
  display: grid;
  row-gap: 24px;
  grid-template-columns: minmax(0, 1fr);

  > :not(:last-child) {
    position: relative;
  }

  @media (max-width: 1199px) {
    > :not(:last-child) {
      ${horizontalSeparator(0, 0)};
    }
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 32px;

    > :nth-child(3n + 1):not(:last-child) {
      ${horizontalSeparator()};
    }
    > :nth-child(3n + 2) {
      ${horizontalSeparator(34, 34)};
    }

    > :nth-child(2n) {
      ${verticalSeparator()};
    }

    > :nth-child(2n + 1):nth-last-child(-n + 2),
    > :nth-child(2n + 1):nth-last-child(-n + 2) ~ * {
      &::after {
        display: none;
      }
    }
  }

  @media (min-width: 1500px) {
    column-gap: 64px;
    > :nth-child(2n) {
      ${verticalSeparator(32)};
    }
  }
`;

export const GridLG = styled(Grid)`
  row-gap: 64px;

  @media (max-width: 1199px) {
    > :not(:last-child) {
      ${horizontalSeparator(0, 0, 32)};
    }
  }

  @media (min-width: 1200px) {
    column-gap: 64px;

    > :nth-child(3n + 1):not(:last-child) {
      ${horizontalSeparator(0, 32, 32)};
    }
    > :nth-child(3n + 2) {
      ${horizontalSeparator(34, 34, 32)};
    }

    > :nth-child(2n) {
      ${verticalSeparator(32)};
    }
  }
`;

export const Layout = styled.div`
  position: relative;
  padding: 20px 32px;
  border-radius: 2px;
  background-color: var(--section-snow-background-color, var(--snow));
`;

export const ChartLayout = styled(Layout)`
  padding-top: 24px;
  margin: -4px 0 8px;
`;

export const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 0 48px 0;
  position: relative;
  &::after {
    content: "";
    position: absolute;
    height: 1px;
    bottom: 32px;
    left: 0;
    right: 0;
    background-color: var(
      --section-periwinkle-grey-background-color,
      var(--periwinkle-grey)
    );
  }
`;

export const ContainerProfile = styled(Typography.BodyText)`
  color: var(--neutrals-granite-neutral-600, #98a0ab);
  font-size: 13px;
  font-weight: 600;
`;

export const BalancedMemory = styled(ContainerProfile)`
  color: var(--neutrals-ebony-light-primary-400, #4a495e);
`;

export const TitleWrapper = styled.div`
  display: flex;
  gap: 10px;
`;

export const MainTitle = styled.h2`
  color: var(--primary-ebony-primary-500, #302f45);
  font-size: 18px;
  font-weight: 700;
`;

export const SticlyHeader = styled(V.Header)`
  margin: 24px 0 16px 0;
  align-items: baseline;
`;

export const InfoWrapper = styled.div`
  .dialog-content {
    width: 380px;
  }
`;
